import React, { useState } from 'react';
import { Building2, CheckCircle, AlertTriangle, TrendingUp, Shield, Clock, Target, Scale, FileText, Users, ChevronDown, DollarSign } from 'lucide-react';
import CTASection from '../../components/CTASection';
import RegistrationModal from '../../components/RegistrationModal';

const CCorpFormationPage = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('registration');

  const openModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const benefits = [
    {
      icon: Shield,
      title: 'Unlimited Growth',
      description: 'No restrictions on shareholders, perfect for raising capital'
    },
    {
      icon: DollarSign,
      title: 'Investor Preferred',
      description: 'VCs and institutional investors prefer C-corp structure'
    },
    {
      icon: TrendingUp,
      title: 'IPO Ready',
      description: 'Can issue multiple stock classes and go public easily'
    },
    {
      icon: CheckCircle,
      title: 'Credibility & Prestige',
      description: 'Most recognized and respected business structure'
    }
  ];

  const packages = [
    {
      name: 'Basic C-Corp',
      price: '$499',
      description: 'Essential incorporation documents and state filing',
      features: [
        'Articles of Incorporation filing',
        'State filing fees included',
        'Corporate bylaws',
        'Stock certificates',
        'Initial meeting minutes',
        '7-10 business days'
      ]
    },
    {
      name: 'Standard C-Corp',
      price: '$799',
      description: 'Complete incorporation with EIN and compliance tools',
      features: [
        'All Basic features',
        'Federal EIN from IRS',
        'Banking resolution',
        'Registered agent (1 year)',
        'Custom bylaws',
        'Corporate kit',
        'Priority processing (5-7 days)'
      ],
      recommended: true
    },
    {
      name: 'Premium C-Corp',
      price: '$1,299',
      description: 'Full-service for startups seeking investment',
      features: [
        'All Standard features',
        'Delaware formation',
        'Preferred & common stock setup',
        '83(b) election assistance',
        'Investor-ready documents',
        'Cap table template',
        'Expedited processing (3-5 days)',
        'Attorney consultation'
      ]
    }
  ];

  const timeline = [
    { time: 'Day 1-2', activity: 'Company name reservation and Articles of Incorporation preparation' },
    { time: 'Day 3-5', activity: 'File with state and obtain certificate of incorporation' },
    { time: 'Day 6-7', activity: 'Federal EIN, corporate bylaws, stock certificates, initial board meeting' },
    { time: 'Day 8-10', activity: 'Complete formation documents, corporate kit, and compliance calendar' }
  ];

  const faqs = [
    {
      question: 'What is a C-Corporation and when should I choose it?',
      answer: 'A C-Corporation is the standard corporate structure offering the strongest liability protection and unlimited growth potential. Choose C-corp if you plan to: raise venture capital or institutional investment, go public (IPO) eventually, have more than 100 shareholders, include foreign investors, offer employee stock options with favorable tax treatment, or reinvest profits in the company. C-corps pay corporate income tax on profits, then shareholders pay personal tax on dividends (double taxation), but this is offset by benefits for high-growth companies.'
    },
    {
      question: 'How is a C-Corp different from an S-Corp or LLC?',
      answer: 'C-Corp: No limits on shareholders, can have different stock classes, preferred by VCs, double taxation but can reinvest profits tax-efficiently. S-Corp: Pass-through taxation (no double tax), limited to 100 US shareholders, one stock class only, cannot have corporate or foreign investors. LLC: Most flexible, pass-through taxation, fewer formalities, but harder to raise institutional capital. For venture-backed startups and companies planning to go public, C-corp is almost always the right choice despite double taxation.'
    },
    {
      question: 'Why do startups choose Delaware C-Corps?',
      answer: 'Over 65% of Fortune 500 companies and 90% of venture-backed startups incorporate in Delaware because: well-established corporate law with decades of precedent, Court of Chancery specializes in business disputes with fast resolution, business-friendly laws protect directors and officers, privacy protection (no need to disclose officer names publicly), easy and fast amendments to corporate structure, and VCs and acquirers prefer Delaware entities. Delaware annual franchise tax is $400-$500, but benefits outweigh costs for growth-oriented companies.'
    },
    {
      question: 'What are the ongoing compliance requirements for C-Corps?',
      answer: 'C-Corporations have more formalities than LLCs: Annual shareholder meeting (required, must document in minutes), Board of Directors meetings (quarterly or as needed, must keep minutes), Annual report filing with state ($50-$800 depending on state), Federal and state income tax returns (Form 1120), Franchise taxes (varies by state, Delaware is $400-$500), Accurate stock ledger and cap table maintenance. While more complex than LLCs, these requirements are manageable with proper systems and provide stronger legal protection and governance structure.'
    },
    {
      question: 'How are C-Corporations taxed?',
      answer: 'C-Corps face \"double taxation\": the corporation pays 21% federal corporate income tax on profits (plus state taxes), then shareholders pay personal income tax (0-20% capital gains or up to 37% ordinary income) on dividends or stock sales. However, C-corps can: deduct employee salaries, benefits, and stock options before paying corporate tax, retain profits in the company without immediate taxation, and take advantage of deductions not available to pass-through entities. For profitable companies reinvesting heavily in growth, the 21% corporate rate can be advantageous. Losses can be carried forward indefinitely to offset future profits.'
    },
    {
      question: 'How do stock options work in C-Corps?',
      answer: 'C-Corps can issue Incentive Stock Options (ISOs) to employees with favorable tax treatment: employees pay no tax on grant or exercise, only capital gains tax on sale if held 2+ years from grant and 1+ year from exercise, and taxed at long-term capital gains rate (0-20%) vs ordinary income (up to 37%). Non-qualified stock options (NSOs) can be issued to contractors and advisors. Proper 409A valuation sets strike price. Employees typically file 83(b) election within 30 days of early exercise to start capital gains holding period. This makes C-corps extremely attractive for recruiting talent with equity compensation.'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#168356]/20 text-[#168356] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              C-Corporation Formation
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Form Your <span className="text-[#168356]">C-Corporation</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              The preferred structure for startups raising venture capital and planning to go public. We handle Delaware incorporation, stock setup, and all compliance requirements.
            </p>
            <div className="flex gap-4">
              <button onClick={() => openModal('registration')} className="bg-[#d29b00] hover:bg-[#a87b00] text-white font-bold px-8 py-4 rounded-lg transition-all">
                Start Your C-Corp
              </button>
              <button onClick={() => openModal('registration')} className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-lg transition-all">
                Compare to S-Corp & LLC
              </button>
            </div>
            <div className="flex items-center gap-6 mt-8 text-sm">
              <span className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#168356]" />
                Investor-Ready Structure
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#168356]" />
                Delaware Formation
              </span>
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#168356]" />
                Unlimited Growth Potential
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="py-12 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">90%</div>
              <div className="text-gray-600 text-sm">Of venture-backed startups</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">65%</div>
              <div className="text-gray-600 text-sm">Of Fortune 500 in Delaware</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">21%</div>
              <div className="text-gray-600 text-sm">Federal corporate tax rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-[#168356] mb-2">Unlimited</div>
              <div className="text-gray-600 text-sm">Shareholders allowed</div>
            </div>
          </div>
        </div>
      </div>

      {/* What Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Why Choose C-Corporation?</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The gold standard for high-growth companies and startups seeking investment
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-[#168356]/10 to-green-50 rounded-xl p-8 border-l-4 border-[#168356]">
              <h3 className="text-2xl font-bold text-black mb-6">Perfect For:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Venture-backed startups</strong> raising capital from VCs and angels</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Companies planning IPO</strong> or eventual public offering</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Businesses with many shareholders</strong> (no 100-person limit)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Companies offering employee stock options</strong> with ISOs</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>International operations</strong> with foreign investors or shareholders</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold text-black mb-6">Key Advantages:</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Unlimited shareholders:</strong> No restrictions on number or type of investors</span>
                </li>
                <li className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Multiple stock classes:</strong> Common and preferred shares for investors</span>
                </li>
                <li className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Retain earnings:</strong> Reinvest profits without distributing to shareholders</span>
                </li>
                <li className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Deductible benefits:</strong> Health insurance, retirement plans fully deductible</span>
                </li>
                <li className="flex items-start gap-3">
                  <Target className="w-5 h-5 text-[#168356] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700"><strong>Perpetual existence:</strong> Corporation continues even if founders leave</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Packages */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">C-Corp Formation Packages</h2>
            <p className="text-lg text-gray-600">From basic filing to investor-ready startups</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div key={index} className={`bg-white rounded-xl p-8 shadow-lg border-2 ${pkg.recommended ? 'border-[#168356] transform scale-105' : 'border-gray-200'}`}>
                {pkg.recommended && (
                  <div className="bg-[#168356] text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-2xl font-bold text-black mb-2">{pkg.name}</h3>
                <div className="text-4xl font-bold text-[#168356] mb-4">{pkg.price}<span className="text-lg text-gray-500"> + state fees</span></div>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-[#168356] flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button onClick={() => openModal('registration')} className={`w-full py-3 rounded-lg font-semibold transition-all ${pkg.recommended ? 'bg-[#d29b00] text-white hover:bg-[#a87b00]' : 'bg-gray-100 text-black hover:bg-gray-200'}`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">C-Corp Formation Timeline</h2>
            <p className="text-lg text-gray-600">From filing to fully formed corporation</p>
          </div>
          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <div key={index} className="flex gap-6 mb-8 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-[#168356] rounded-full flex items-center justify-center text-white font-bold">
                    {index + 1}
                  </div>
                  {index < timeline.length - 1 && (
                    <div className="w-0.5 h-16 bg-[#168356]"></div>
                  )}
                </div>
                <div className="flex-1 pb-8">
                  <div className="text-[#168356] font-semibold mb-2">{item.time}</div>
                  <div className="text-gray-700 text-lg">{item.activity}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Benefits of C-Corporation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#168356] rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-black mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-100 transition-colors"
                >
                  <span className="text-left font-semibold text-black pr-8">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-[#168356] flex-shrink-0 transition-transform ${openFAQ === index ? 'rotate-180' : ''}`} />
                </button>
                {openFAQ === index && (
                  <div className="px-6 pb-4 text-gray-700 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType={modalType}
      />
    </div>
  );
};

export default CCorpFormationPage;
